import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Price } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { useHistoriqueContact, useOffres } from '../../api/offres.js';
import { galerieALaction } from '../../auth/sujet.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { useShellContext } from '../shell/context.js';
import { NouvelleOffreDialog } from './NouvelleOffreDialog.js';
import styles from './HistoriqueContact.module.css';

export function HistoriqueContact({
  galerieSlug,
  contactId,
  contactNom,
}: {
  galerieSlug: string;
  contactId: string;
  contactNom: string;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerie } = useShellContext();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const historique = useHistoriqueContact(galerieSlug, contactId);
  const [nouvelle, setNouvelle] = useState(false);
  return (
    <section className={`${styles.section} ${styles.sectionCarte}`}>
      <h2>{t('regie:offres.historique.titre')}</h2>
      {historique.isLoading ? (
        <p role="status">{t('commun:chargement')}</p>
      ) : historique.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : historique.data ? (
        <>
          <h3>{t('regie:offres.historique.achats')}</h3>
          {historique.data.achats.factures === 'non_incluses' ? (
            <p>{t('regie:offres.historique.facturesNonIncluses')}</p>
          ) : (
            historique.data.achats.factures.map((f) => (
              <div key={f.id} className={styles.entree}>
                <Link to={`/${galerieSlug}/ventes/${f.id}`}>{f.numero}</Link>
                {f.oeuvres.map((o) => (
                  <ObjetCartel key={o.id} titre={o.titre} taille="sm" />
                ))}
                {f.totalTtcCentimes === null ? (
                  <span>—</span>
                ) : (
                  <Price cents={f.totalTtcCentimes} currency={f.devise} lang={langue} />
                )}
                {f.emiseLe ? (
                  <time dateTime={f.emiseLe}>{formatDate(new Date(f.emiseLe), langue)}</time>
                ) : null}
              </div>
            ))
          )}
          {historique.data.achats.ventesSimples.map((v) => (
            <div className={styles.entree} key={v.oeuvreId}>
              <Link to={`/${galerieSlug}/oeuvres/${v.oeuvreId}`}>
                <ObjetCartel titre={v.titre} taille="sm" />
              </Link>
              <Price cents={v.prixCentimes} currency={v.devise} lang={langue} />
              <time dateTime={v.venduLe}>{formatDate(new Date(v.venduLe), langue)}</time>
            </div>
          ))}
          <h3>{t('regie:offres.historique.interets')}</h3>
          {historique.data.interets.length ? (
            <ul className={styles.liste}>
              {historique.data.interets.map((entree) => (
                <li className={styles.entree} key={`${entree.type}-${entree.id}`}>
                  <div>
                    <Badge tone="neutral">
                      {t(`regie:offres.historique.types.${entree.type}`)}
                    </Badge>{' '}
                    <time dateTime={entree.date}>{formatDate(new Date(entree.date), langue)}</time>
                  </div>
                  {entree.type === 'offre' ? (
                    <Link to={`/${galerieSlug}/offres/${entree.id}`}>{entree.numero}</Link>
                  ) : null}
                  {entree.oeuvres.map((o) => (
                    <Link to={`/${galerieSlug}/oeuvres/${o.id}`} key={o.id}>
                      <ObjetCartel titre={o.titre} taille="sm" />
                    </Link>
                  ))}
                  <span>
                    {entree.type === 'offre'
                      ? t(`regie:offres.etats.${entree.etat}`)
                      : t(`regie:offres.historique.etats.${entree.etat}`, {
                          defaultValue: entree.etat,
                        })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{t('regie:offres.historique.vide')}</p>
          )}
        </>
      ) : null}
      {galerieALaction(galerie, 'offers:write') ? (
        <Button variant="secondary" onClick={() => setNouvelle(true)}>
          {t('regie:offres.nouvelle.titre')}
        </Button>
      ) : null}
      {nouvelle ? (
        <NouvelleOffreDialog
          open
          onOpenChange={setNouvelle}
          contactInitial={{ id: contactId, nom: contactNom }}
        />
      ) : null}
    </section>
  );
}
export function OffresOeuvre({ galerieSlug, oeuvreId }: { galerieSlug: string; oeuvreId: string }) {
  const { t } = useTranslation(['regie', 'commun']);
  const offres = useOffres(galerieSlug, { oeuvreId, closes: false });
  return (
    <section className={styles.section}>
      <h2>{t('regie:offres.historique.enCours')}</h2>
      {offres.isLoading ? (
        <p role="status">{t('commun:chargement')}</p>
      ) : offres.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : offres.data?.offres.length ? (
        <ul className={styles.liste}>
          {offres.data.offres
            .filter((o) => ['brouillon', 'envoyee', 'acceptee'].includes(o.etat))
            .map((o) => (
              <li key={o.id}>
                <Link to={`/${galerieSlug}/offres/${o.id}`}>
                  {o.numero} · {o.contactNom}
                </Link>{' '}
                <Badge>{t(`regie:offres.etats.${o.etat}`)}</Badge>
              </li>
            ))}
        </ul>
      ) : (
        <p>{t('regie:offres.historique.vide')}</p>
      )}
    </section>
  );
}
